import {styled} from "@mui/material/styles";
import {type MouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

const EXPAND_TEXT = "show more";
const COLLAPSE_TEXT = "show less";
const ELLIPSIS_TEXT = "... ";
const HEIGHT_TOLERANCE_PX = 1;
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const Root = styled("span")({
    display: "block",
    position: "relative",
});

const Measure = styled("span")({
    position: "absolute",
    top: 0,
    left: 0,
    visibility: "hidden",
    pointerEvents: "none",
    overflowWrap: "break-word",
});

const Toggle = styled("button")(({theme}) => ({
    appearance: "none",
    border: 0,
    padding: 0,
    background: "none",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    font: "inherit",
    lineHeight: "inherit",
    verticalAlign: "baseline",
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
        textDecoration: "underline",
    },
}));

function trimAtWordBoundary(text: string) {
    const trimmedText = text.trimEnd();
    const lastSpaceIndex = trimmedText.lastIndexOf(" ");
    if (lastSpaceIndex > 0 && lastSpaceIndex >= trimmedText.length - 20) {
        return trimmedText.slice(0, lastSpaceIndex);
    }
    return trimmedText;
}

export interface ProjectCardExpandCollapseProps {
    text: string;
    previewHeight: number;
    onExpandedChange?: (expanded: boolean) => void;
    renderText?: (text: string) => ReactNode;
}

export function ProjectCardExpandCollapse({
    text,
    previewHeight,
    onExpandedChange,
    renderText = (value) => value,
}: ProjectCardExpandCollapseProps) {
    const rootRef = useRef<HTMLSpanElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [collapsedText, setCollapsedText] = useState(text);
    const [shouldExpand, setShouldExpand] = useState(false);

    const measure = useCallback(() => {
        const root = rootRef.current;
        const measureElement = measureRef.current;
        if (!root || !measureElement || root.clientWidth === 0) {
            return;
        }

        measureElement.style.width = `${root.clientWidth}px`;
        measureElement.textContent = text;
        if (measureElement.scrollHeight <= previewHeight + HEIGHT_TOLERANCE_PX) {
            setCollapsedText(text);
            setShouldExpand(false);
            if (expanded) {
                setExpanded(false);
                onExpandedChange?.(false);
            }
            return;
        }

        const characters = Array.from(text);
        let lowerBound = 0;
        let upperBound = characters.length;

        while (lowerBound < upperBound) {
            const candidateLength = Math.ceil((lowerBound + upperBound) / 2);
            const candidate = characters.slice(0, candidateLength).join("").trimEnd();
            measureElement.textContent = `${candidate}${ELLIPSIS_TEXT}${EXPAND_TEXT}`;

            if (measureElement.scrollHeight <= previewHeight + HEIGHT_TOLERANCE_PX) {
                lowerBound = candidateLength;
            } else {
                upperBound = candidateLength - 1;
            }
        }

        setCollapsedText(trimAtWordBoundary(characters.slice(0, lowerBound).join("")));
        setShouldExpand(true);
    }, [expanded, onExpandedChange, previewHeight, text]);

    useIsomorphicLayoutEffect(() => {
        measure();

        const root = rootRef.current;
        if (!root) {
            return;
        }

        const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        resizeObserver?.observe(root);

        let cancelled = false;
        void document.fonts?.ready.then(() => {
            if (!cancelled) {
                measure();
            }
        });

        return () => {
            cancelled = true;
            resizeObserver?.disconnect();
        };
    }, [measure]);

    const toggleExpanded = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setExpanded((currentExpanded) => {
                const nextExpanded = !currentExpanded;
                onExpandedChange?.(nextExpanded);
                return nextExpanded;
            });
        },
        [onExpandedChange],
    );

    return (
        <Root ref={rootRef}>
            <Measure ref={measureRef} aria-hidden />
            {expanded ? (
                <>
                    {renderText(text)}{" "}
                    <Toggle type="button" onClick={toggleExpanded} aria-expanded>
                        {COLLAPSE_TEXT}
                    </Toggle>
                </>
            ) : shouldExpand ? (
                <>
                    {renderText(collapsedText)}
                    {ELLIPSIS_TEXT}
                    <Toggle type="button" onClick={toggleExpanded} aria-expanded={false}>
                        {EXPAND_TEXT}
                    </Toggle>
                </>
            ) : (
                renderText(text)
            )}
        </Root>
    );
}
